import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  ModalComponent,
  RadioCardComponent,
  RadioCardGroupComponent,
} from '@catch/general-ui';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  CREDIT_COST_SOURCING_AUTOPILOT,
  CREDIT_COST_SOURCING_LEADS,
} from '../../constants/credit-cost.constant';
import { CreditConsumptionClickDirective } from '../../directives/credit-consumption-click.directive';
import { SourcedJobsCampaignMode } from '../../enums/sourced-jobs-campaign-mode.enum';
import { FontawesomeService } from '../../services/fontawesome.service';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'app-sourcing-campaign-booking-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ModalComponent,
    RadioCardComponent,
    RadioCardGroupComponent,
    TranslocoPipe,
    ButtonComponent,
    CreditConsumptionClickDirective,
    DecimalPipe,
  ],
  templateUrl: './sourcing-campaign-booking-modal.component.html',
  styleUrl: './sourcing-campaign-booking-modal.component.scss',
})
export class SourcingCampaignBookingModalComponent {
  private readonly modal = viewChild.required<ModalComponent>('modal');
  readonly fontawesomeService = inject(FontawesomeService);

  readonly requiresCredits = input(true);
  readonly confirmed = output<SourcedJobsCampaignMode>();

  readonly SourcedJobsCampaignMode = SourcedJobsCampaignMode;
  readonly CREDIT_COST_SOURCING_LEADS = CREDIT_COST_SOURCING_LEADS;
  readonly CREDIT_COST_SOURCING_AUTOPILOT = CREDIT_COST_SOURCING_AUTOPILOT;

  readonly selectedMode = signal<SourcedJobsCampaignMode | null>(null);

  readonly creditAmount = computed(() => {
    switch (this.selectedMode()) {
      case SourcedJobsCampaignMode.LEADS:
        return CREDIT_COST_SOURCING_LEADS;
      case SourcedJobsCampaignMode.AUTOPILOT:
        return CREDIT_COST_SOURCING_AUTOPILOT;
      default:
        return 0;
    }
  });

  /**
   * Opens the sourcing campaign booking modal and resets the selection.
   * @returns {void}
   */
  open(): void {
    this.selectedMode.set(null);
    this.modal().open();
  }

  /**
   * Closes the modal without confirming.
   * @returns {void}
   */
  close(): void {
    this.modal().close();
  }

  /**
   * Updates the selected campaign mode from the radio card group.
   * @param {SourcedJobsCampaignMode | null} mode - Selected mode
   * @returns {void}
   */
  onModeChange(mode: unknown): void {
    this.selectedMode.set(mode as SourcedJobsCampaignMode | null);
  }

  /**
   * Confirms the selected mode and closes the modal.
   * @returns {void}
   */
  onConfirm(): void {
    const mode = this.selectedMode();
    if (!mode) {
      return;
    }
    this.confirmed.emit(mode);
    this.close();
  }
}
